import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  CheckCircleIcon, 
  XCircleIcon,
  AlertCircleIcon,
  FileTextIcon,
  MessageSquareIcon,
  PrinterIcon,
  ChevronLeftIcon,
  ThumbsUpIcon,
  ThumbsDownIcon,
  RefreshCwIcon
} from "lucide-react";
import { RFQService, RFQRequest, RFQComparison } from "@/lib/rfq-service";
import { RFQComparisonTable } from "@/polymet/components/rfq-comparison";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";

export default function RFQDetailPage() {
  const { rfqId } = useParams<{ rfqId: string }>();
  const [rfq, setRfq] = useState<RFQRequest | null>(null);
  const [comparison, setComparison] = useState<RFQComparison | null>(null);
  const [loading, setLoading] = useState(true);
  const [comparisonLoading, setComparisonLoading] = useState(true);
  const [selectedResponseId, setSelectedResponseId] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'accept' | 'reject' | 'negotiate' | null>(null);
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const rfqService = new RFQService();
  
  useEffect(() => {
    const loadRFQ = async () => {
      if (!rfqId) return;
      
      try {
        setLoading(true);
        const rfqData = await rfqService.getRFQById(rfqId);
        setRfq(rfqData);
      } catch (error) {
        console.error("Error loading RFQ details:", error);
        toast({
          title: "Error",
          description: "Failed to load RFQ details. Please try again.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadRFQ();
  }, [rfqId]);
  
  useEffect(() => {
    const loadComparison = async () => {
      if (!rfqId) return;
      
      try {
        setComparisonLoading(true);
        const comparisonData = await rfqService.getRFQComparison(rfqId);
        setComparison(comparisonData);
      } catch (error) {
        console.error("Error loading RFQ comparison:", error);
        toast({
          title: "Error",
          description: "Failed to load quote comparison. Please try again.",
          variant: "destructive"
        });
      } finally {
        setComparisonLoading(false);
      }
    };
    
    loadComparison();
  }, [rfqId]);
  
  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString();
  };
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "draft":
        return <Badge variant="outline">Draft</Badge>;
      case "published":
        return <Badge className="bg-blue-500">Active</Badge>;
      case "closed":
        return <Badge className="bg-yellow-500">Closed</Badge>;
      case "fulfilled":
        return <Badge className="bg-green-500">Fulfilled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };
  
  const handleStatusChange = async (newStatus: RFQRequest['status']) => {
    if (!rfq || !rfqId) return;
    
    try {
      const success = await rfqService.updateRFQRequestStatus(rfqId, newStatus);
      
      if (success) {
        setRfq({ ...rfq, status: newStatus });
        toast({
          title: "Success",
          description: `RFQ status updated to ${newStatus}`
        });
      } else {
        throw new Error("Failed to update RFQ status");
      }
    } catch (error) {
      console.error("Error updating RFQ status:", error);
      toast({
        title: "Error",
        description: "Failed to update RFQ status. Please try again.",
        variant: "destructive"
      });
    }
  };
  
  const handleResponseAction = async (responseId: string, status: 'accepted' | 'rejected' | 'negotiating') => {
    if (!rfqId || !responseId) return;
    
    try {
      const success = await rfqService.updateRFQResponseStatus(responseId, status);
      
      if (success) {
        // Reload comparison data to reflect the changes
        const updatedComparison = await rfqService.getRFQComparison(rfqId);
        setComparison(updatedComparison);
        
        // If accepting a quote, mark the RFQ as fulfilled
        if (status === 'accepted') {
          await rfqService.updateRFQRequestStatus(rfqId, 'fulfilled');
          if (rfq) setRfq({ ...rfq, status: 'fulfilled' });
        }
        
        toast({
          title: "Success",
          description: `Quote ${
            status === 'accepted' 
              ? 'accepted' 
              : status === 'rejected' 
                ? 'rejected' 
                : 'sent for negotiation'
          } successfully`
        });
        
        // Close the confirmation dialog
        setConfirmDialogOpen(false);
      } else {
        throw new Error(`Failed to ${status} quote response`);
      }
    } catch (error) {
      console.error(`Error ${status} quote response:`, error);
      toast({
        title: "Error",
        description: `Failed to process your action. Please try again.`,
        variant: "destructive"
      });
    }
  };
  
  const openConfirmationDialog = (responseId: string, type: 'accept' | 'reject' | 'negotiate') => {
    setSelectedResponseId(responseId);
    setActionType(type);
    setConfirmDialogOpen(true);
  };
  
  if (!user) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Alert>
          <AlertTitle>Authentication Required</AlertTitle>
          <AlertDescription>
            Please log in to view RFQ details.
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="space-y-4">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-4 w-1/4" />
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-1/4" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  if (!rfq) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Alert>
          <AlertTitle>RFQ Not Found</AlertTitle>
          <AlertDescription>
            The requested RFQ could not be found. It may have been deleted or you don't have permission to view it.
          </AlertDescription>
        </Alert>
        <Button 
          className="mt-4"
          variant="outline"
          onClick={() => navigate(-1)}
        >
          <ChevronLeftIcon className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">{rfq.title}</h1>
          <div className="flex items-center gap-2 mt-2">
            <p className="text-gray-500">Created on {formatDate(rfq.created_at)}</p>
            {getStatusBadge(rfq.status)}
          </div>
        </div>
        
        <div className="flex gap-2 mt-4 md:mt-0">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
          >
            <ChevronLeftIcon className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          {rfq.status === "draft" && (
            <Button
              onClick={() => handleStatusChange("published")}
            >
              Publish RFQ
            </Button>
          )}
          
          {rfq.status === "published" && (
            <Button
              variant="outline"
              onClick={() => handleStatusChange("closed")}
            >
              Close RFQ
            </Button>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card>
          <CardHeader>
            <CardTitle>RFQ Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Description</h3>
              <p>{rfq.description}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Quantity</h3>
                <p>{rfq.quantity}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Budget Range</h3>
                <p>
                  {rfq.budget_min && rfq.budget_max 
                    ? `$${rfq.budget_min} - $${rfq.budget_max}`
                    : "Not specified"
                  }
                </p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Delivery Location</h3>
                <p>{rfq.delivery_location || "Not specified"}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Required By</h3>
                <p>{rfq.delivery_date_required ? formatDate(rfq.delivery_date_required) : "Not specified"}</p>
              </div>
            </div>
          </CardContent>
          <CardFooter>
            {rfq.specifications && Object.keys(rfq.specifications).length > 0 && (
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => document.getElementById("specifications-section")?.scrollIntoView({ behavior: "smooth" })}
              >
                View Specifications
              </Button>
            )}
          </CardFooter>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Current Status</h3>
                <div className="mt-1">
                  {getStatusBadge(rfq.status)}
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Expiry Date</h3>
                <p>{rfq.expiry_date ? formatDate(rfq.expiry_date) : "No deadline"}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Responses</h3>
                <p>{comparison?.responses.length || 0} quotes received</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Created By</h3>
                <p>{rfq.user_id === user.id ? "You" : "Another user"}</p>
              </div>
            </div>
          </CardContent>
          <CardFooter>
            {rfq.status === "published" && (
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => handleStatusChange("closed")}
              >
                Close RFQ
              </Button>
            )}
            {rfq.status === "closed" && (
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => handleStatusChange("published")}
              >
                <RefreshCwIcon className="h-4 w-4 mr-2" />
                Reopen RFQ
              </Button>
            )}
          </CardFooter>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-3">
              <Button 
                variant="outline" 
                className="justify-start"
                onClick={() => navigate(`/edit-rfq/${rfq.id}`)}
              >
                <FileTextIcon className="h-4 w-4 mr-2" />
                Edit RFQ
              </Button>
              
              <Button 
                variant="outline" 
                className="justify-start"
                onClick={() => navigate(`/messages/rfq/${rfq.id}`)}
              >
                <MessageSquareIcon className="h-4 w-4 mr-2" />
                Messages
              </Button>
              
              <Button 
                variant="outline" 
                className="justify-start"
                onClick={() => window.print()}
              >
                <PrinterIcon className="h-4 w-4 mr-2" />
                Print / Export
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div id="specifications-section" className="mb-8">
        <h2 className="text-xl font-bold mb-4">Technical Specifications</h2>
        {rfq.specifications && Object.keys(rfq.specifications).length > 0 ? (
          <Card>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                {Object.entries(rfq.specifications).map(([key, value]) => (
                  <div key={key} className="flex flex-col">
                    <h3 className="text-sm font-medium text-gray-500">{key}</h3>
                    <p>{value}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-6">
              <p className="text-gray-500">No technical specifications provided for this RFQ.</p>
            </CardContent>
          </Card>
        )}
      </div>
      
      <div className="mb-8">
        <h2 className="text-xl font-bold mb-4">Quote Comparison</h2>
        {comparisonLoading ? (
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            </CardContent>
          </Card>
        ) : comparison && comparison.responses.length > 0 ? (
          <RFQComparisonTable 
            comparison={comparison} 
            onSelectResponse={setSelectedResponseId}
          />
        ) : (
          <Card>
            <CardContent className="p-6">
              <p className="text-gray-500">No quotes have been received yet for this RFQ.</p>
            </CardContent>
          </Card>
        )}
      </div>
      
      {selectedResponseId && comparison && (
        <div className="mt-8">
          <h2 className="text-xl font-bold mb-4">Selected Quote</h2>
          <Card>
            <CardHeader>
              <CardTitle>Quote Actions</CardTitle>
              <CardDescription>
                Take action on the selected quote from {
                  comparison.responses.find(r => r.id === selectedResponseId)?.supplier.name
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Button 
                  className="flex-1"
                  onClick={() => openConfirmationDialog(selectedResponseId, 'accept')}
                >
                  <ThumbsUpIcon className="h-4 w-4 mr-2" />
                  Accept Quote
                </Button>
                
                <Button 
                  variant="outline"
                  className="flex-1"
                  onClick={() => openConfirmationDialog(selectedResponseId, 'negotiate')}
                >
                  <MessageSquareIcon className="h-4 w-4 mr-2" />
                  Negotiate
                </Button>
                
                <Button 
                  variant="destructive"
                  className="flex-1"
                  onClick={() => openConfirmationDialog(selectedResponseId, 'reject')}
                >
                  <ThumbsDownIcon className="h-4 w-4 mr-2" />
                  Reject Quote
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'accept' && "Accept Quote"}
              {actionType === 'reject' && "Reject Quote"}
              {actionType === 'negotiate' && "Negotiate Quote"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p>
              {actionType === 'accept' && (
                "Are you sure you want to accept this quote? This will mark the RFQ as fulfilled and notify the supplier."
              )}
              {actionType === 'reject' && (
                "Are you sure you want to reject this quote? This will notify the supplier that their quote was not selected."
              )}
              {actionType === 'negotiate' && (
                "This will notify the supplier that you'd like to negotiate the terms of their quote."
              )}
            </p>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
            >
              Cancel
            </Button>
            
            <Button
              variant={actionType === 'reject' ? "destructive" : "default"}
              onClick={() => {
                if (selectedResponseId && actionType) {
                  const status = 
                    actionType === 'accept' 
                      ? 'accepted' 
                      : actionType === 'reject' 
                        ? 'rejected' 
                        : 'negotiating';
                  
                  handleResponseAction(selectedResponseId, status);
                }
              }}
            >
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
